import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useFetcher } from 'react-router';
import { useAtomValue } from 'jotai';
import { Check } from 'lucide-react';
import { ReactComponent as GoogleCalendarIcon } from '../../icons/google-calendar.svg';
import { ReactComponent as AppleCalendarIcon } from '../../icons/apple-calendar.svg';
import { ReactComponent as OutlookCalendarIcon } from '../../icons/outlook-calendar.svg';
import { ReactComponent as DownloadIcsIcon } from '../../icons/download-ics.svg';
import type { StepContentProps } from '~/ui-rocco/multi-step-form';
import { useTracker } from '@archipro-website/tracker';
import { uiContextAtomDisplayMode } from '@rocco/states/uiContextAtomDisplayMode';
import { Avatar, AvatarFallback, AvatarImage } from '@rocco/components/avatar';
import { Button } from '@rocco/components/button';
import { HDE_CLIENT_DIRECTOR_FALLBACK_API_ROUTE } from '~/routes/remix-api.hde.client-director-fallback';

type HDERegistrationType = 'Homeowner' | 'Professional' | 'Exhibitor';

/** Email allowlist for fallback client directors when no HubSpot director is assigned. */
const CLIENT_DIRECTOR_FALLBACK_EMAILS = [
    'joel@archipro.co.nz',
    'megan.flynn@archipro.co.nz',
] as const;

const DEFAULT_CLIENT_DIRECTOR_TITLE = 'Client Director';

/** Team member shape returned by the build-hub team-members-suggestions API. */
interface FetchedTeamMember {
    ID: number;
    Name?: string | null;
    JobTitle?: string | null;
    Email?: string | null;
    Phone?: string | null;
    MemberImage?: {
        Avatar?: string | null;
    } | null;
}

interface HDESuccessStepData {
    onDone?: () => void;
    registrationType?: HDERegistrationType;
    companyName?: string;
    calendarLink?: string;
    googleCalendarLink?: string;
    outlookCalendarLink?: string;
    clientDirector?: {
        ID: string | number;
        Name?: string | null;
        JobTitle?: string | null;
        Email?: string | null;
        Phone?: string | null;
        MemberImage?: {
            ID?: string | null;
            LargeAvatar?: string | null;
            TeamMemberDisplay?: string | null;
        } | null;
    } | null;
    clientDirectorDisplayName?: string;
    /** When false, show loading until submission succeeds. */
    isSubmitSuccess?: boolean;
}

function getInitialsFromName(name: string): string {
    return (
        name
            .trim()
            .split(/\s+/)
            .map((part: string) => part[0] ?? '')
            .join('')
            .slice(0, 2)
            .toUpperCase() || 'CD'
    );
}

/** Normalise assigned client director or fetched team member for display. */
function toDisplayContact(
    contact:
        | NonNullable<HDESuccessStepData['clientDirector']>
        | FetchedTeamMember,
    clientDirectorDisplayName?: string,
) {
    const name = (
        contact.Name ??
        clientDirectorDisplayName ??
        'Client Director'
    ).trim();
    const email = (contact.Email ?? '').trim();
    const jobTitle =
        (contact.JobTitle ?? '').trim() || DEFAULT_CLIENT_DIRECTOR_TITLE;
    const phone = (contact.Phone ?? '').trim();
    const avatarUrl =
        'LargeAvatar' in (contact.MemberImage ?? {})
            ? (contact.MemberImage as { LargeAvatar?: string | null })
                  ?.LargeAvatar
            : (contact.MemberImage as { Avatar?: string | null } | undefined)
                  ?.Avatar;
    return {
        id: String(contact.ID),
        name: name || 'Client Director',
        email,
        jobTitle,
        phone,
        avatarUrl: avatarUrl ?? null,
        initials: getInitialsFromName(name || 'CD'),
    };
}

/** Exhibitor-only success view with client director contact. */
function ExhibitorSuccessView({
    onDone,
    clientDirector,
    companyName,
    clientDirectorDisplayName,
}: {
    onDone?: () => void;
    clientDirector?: HDESuccessStepData['clientDirector'];
    companyName?: string;
    clientDirectorDisplayName?: string;
}) {
    const fetcher = useFetcher<{
        teamMembers?: FetchedTeamMember[];
    }>();
    const hasLoadedFallbackRef = useRef(false);

    const fallbackTeamMembers = useMemo(() => {
        if (clientDirector) return [];
        const data = fetcher.data;
        return data?.teamMembers ?? [];
    }, [clientDirector, fetcher.data]);

    useEffect(() => {
        if (clientDirector || hasLoadedFallbackRef.current) {
            return;
        }

        if (fetcher.state === 'idle' && !fetcher.data) {
            hasLoadedFallbackRef.current = true;
            const params = new URLSearchParams({
                emails: CLIENT_DIRECTOR_FALLBACK_EMAILS.join(','),
            });
            fetcher.load(
                `${HDE_CLIENT_DIRECTOR_FALLBACK_API_ROUTE}?${params.toString()}`,
            );
        }
    }, [clientDirector, fetcher, fetcher.data, fetcher.state]);

    const contacts = useMemo(() => {
        if (clientDirector) {
            return [
                toDisplayContact(clientDirector, clientDirectorDisplayName),
            ];
        }
        if (fallbackTeamMembers.length > 0) {
            return fallbackTeamMembers.map((m) => toDisplayContact(m));
        }
        return [];
    }, [clientDirector, clientDirectorDisplayName, fallbackTeamMembers]);

    const isLoading = !clientDirector && fetcher.state !== 'idle';

    return (
        <div className="relative flex min-h-0 flex-1 flex-col bg-[#EAEAEA]">
            <p className="text-xs tracking-wide text-[#6B6B6B]">
                We&apos;ll be in touch.
            </p>

            <div className="mt-8 md:mt-12">
                <h1 className="text-[1.25rem] font-semibold uppercase leading-[1.2] tracking-tight text-[#161616] md:text-[1.375rem]">
                    Great to see your interest in exhibiting at the Home Design
                    Evening.
                </h1>
                <p className="mt-4 text-[0.9375rem] leading-relaxed text-[#2A2A2A]">
                    An ArchiPro Client Director will be in touch to discuss
                    {companyName ? ` ${companyName}'s` : ' the'} available
                    opportunities.
                </p>
            </div>

            <div className="mt-8 flex flex-col gap-6">
                {isLoading && (
                    <p className="text-[0.9375rem] text-[#6B6B6B]">
                        Loading your contact...
                    </p>
                )}
                {!isLoading &&
                    contacts.map((contact) => (
                        <div
                            key={contact.id}
                            className="flex items-start gap-4"
                        >
                            <Avatar
                                size="square56"
                                className="shrink-0 border-2"
                            >
                                {contact.avatarUrl ? (
                                    <AvatarImage
                                        src={contact.avatarUrl}
                                        alt={contact.name}
                                    />
                                ) : null}
                                <AvatarFallback className="bg-[#E5E5E5] text-[#2A2A2A]">
                                    {contact.initials}
                                </AvatarFallback>
                            </Avatar>
                            <div className="flex min-w-0 flex-1 flex-col gap-1">
                                <span className="text-[0.9375rem] font-medium text-[#161616]">
                                    {contact.name}
                                </span>
                                {contact.email && (
                                    <a
                                        href={`mailto:${contact.email}`}
                                        className="text-[0.875rem] text-[#2A2A2A] underline md:hover:text-[#161616]"
                                    >
                                        {contact.email}
                                    </a>
                                )}
                                {contact.phone && (
                                    <span className="text-[0.875rem] text-[#2A2A2A]">
                                        {contact.phone}
                                    </span>
                                )}
                            </div>
                        </div>
                    ))}
            </div>

            <div className="flex-1" />

            {onDone && (
                <div className="flex flex-col items-center justify-center px-10 pb-20">
                    <Button
                        onClick={onDone}
                        color="gray"
                        className="h-[2.8125rem] min-w-[11.125rem] rounded-full bg-[#38383B] font-medium text-white md:hover:bg-[#38383B]/90"
                    >
                        Close
                    </Button>
                </div>
            )}
        </div>
    );
}

function CalendarButton({
    href,
    label,
    icon,
    openInNewTab = true,
}: {
    href: string;
    label: string;
    icon: React.ReactNode;
    /** When false, link opens in same window. Required for webcal:// on iOS Safari. */
    openInNewTab?: boolean;
}) {
    return (
        <a
            href={href}
            {...(openInNewTab && {
                target: '_blank',
                rel: 'noopener noreferrer',
            })}
            className="flex h-10 w-[14.5rem] items-center justify-center gap-3 rounded-full bg-white px-4 text-sm font-medium text-[#2A2A2A] shadow-sm transition-opacity hover:opacity-90"
        >
            {icon}
            <span>{label}</span>
        </a>
    );
}

function HomeownerSuccessView({
    data,
}: {
    data: HDESuccessStepData | undefined;
}) {
    const [isAppleDevice, setIsAppleDevice] = useState(false);

    useEffect(() => {
        setIsAppleDevice(
            /iPad|iPhone|iPod|Macintosh/.test(navigator.userAgent),
        );
    }, []);

    const hasCalendarLinks =
        data?.googleCalendarLink ||
        data?.outlookCalendarLink ||
        data?.calendarLink;

    /** webcal:// opens directly in Apple Calendar on Apple systems. */
    const iosCalendarUrl =
        data?.calendarLink && typeof window !== 'undefined'
            ? new URL(data.calendarLink, window.location.origin).href.replace(
                  /^https?:/,
                  'webcal:',
              )
            : undefined;

    return (
        <div className="relative flex min-h-0 flex-1 flex-col bg-[#EAEAEA]">
            <p className="text-center text-xs tracking-wide text-[#313131]">
                You&apos;re in.
            </p>

            <div className="mt-10 md:mt-20">
                <h1 className="text-[1.3125rem] font-medium uppercase leading-[1.1] tracking-tight text-[#161616] md:w-80">
                    <Check className="mb-0.5 mr-2 inline size-5" />
                    <>
                        You&apos;re registered.
                        <br />A confirmation is on <br />
                        its way to your inbox.
                    </>
                </h1>
            </div>

            <div className="mt-6 flex flex-col gap-2 md:mt-10">
                <div className="flex items-center gap-3.5 py-2.5 text-3.5 font-medium md:w-80">
                    A confirmation is on its way to your inbox. A ticket will
                    follow one week before the event. Add this event to your
                    Calendar:
                </div>
                {hasCalendarLinks && (
                    <div className="flex flex-col gap-3 py-2.5">
                        <div className="flex flex-col items-center gap-2">
                            {data?.googleCalendarLink && (
                                <CalendarButton
                                    href={data.googleCalendarLink}
                                    label="Google Calendar"
                                    icon={
                                        <GoogleCalendarIcon className="size-6 shrink-0" />
                                    }
                                />
                            )}
                            {isAppleDevice && iosCalendarUrl && (
                                <CalendarButton
                                    href={iosCalendarUrl}
                                    label="iOS Calendar"
                                    icon={
                                        <AppleCalendarIcon className="size-6 shrink-0" />
                                    }
                                    openInNewTab={false}
                                />
                            )}
                            {data?.outlookCalendarLink && (
                                <CalendarButton
                                    href={data.outlookCalendarLink}
                                    label="Outlook Calendar"
                                    icon={
                                        <OutlookCalendarIcon className="size-6 shrink-0" />
                                    }
                                />
                            )}
                            {!isAppleDevice && data?.calendarLink && (
                                <CalendarButton
                                    href={data.calendarLink}
                                    label="Download ICS"
                                    icon={
                                        <DownloadIcsIcon className="size-6 shrink-0" />
                                    }
                                />
                            )}
                        </div>
                    </div>
                )}
            </div>

            <div className="flex-1" />

            {data?.onDone && (
                <div className="flex items-center justify-center gap-3 px-10 pb-20">
                    <Link
                        to="/"
                        onClick={data.onDone}
                        className="inline-flex h-[2.8125rem] w-[11.125rem] shrink-0 items-center justify-center rounded-full border-0 bg-[#38383B] font-medium text-white md:hover:bg-[#38383B]/20"
                    >
                        Explore ArchiPro
                    </Link>
                </div>
            )}
        </div>
    );
}

function SubmittingView() {
    return (
        <div className="relative flex min-h-0 flex-1 flex-col items-center justify-center bg-[#EAEAEA]">
            <p className="text-[0.9375rem] text-[#6B6B6B]">
                Submitting your registration...
            </p>
        </div>
    );
}

export function HDESuccessStep({ setIsValid, initialData }: StepContentProps) {
    const data = initialData as HDESuccessStepData | undefined;
    const tracker = useTracker();
    const { isMobile } = useAtomValue(uiContextAtomDisplayMode);
    const hasTracked = useRef(false);

    useEffect(() => {
        setIsValid(true);
    }, [setIsValid]);

    useEffect(() => {
        if (data?.isSubmitSuccess !== true || hasTracked.current) return;
        hasTracked.current = true;

        const source = data?.registrationType ?? 'Homeowner';
        const deviceType = isMobile ? 'Mobile' : 'Desktop';
        const url = new URL(window.location.href);

        void tracker.log('HdeSuccessRegister', {
            data: {
                Source: source,
                DeviceType: deviceType,
            },
            ga4Data: {
                source,
                device_type: deviceType,
            },
            url,
        });

        void tracker.log('HDESubmission', {
            data: {
                Source: source,
                DeviceType: deviceType,
            },
            targetTracker: 'facebookTracker',
            url,
        });
    }, [tracker, isMobile, data?.registrationType, data?.isSubmitSuccess]);

    if (data?.isSubmitSuccess !== true) {
        return <SubmittingView />;
    }

    const registrationType = data?.registrationType;

    if (registrationType === 'Exhibitor') {
        return (
            <ExhibitorSuccessView
                onDone={data?.onDone}
                clientDirector={data?.clientDirector}
                companyName={data?.companyName}
                clientDirectorDisplayName={data?.clientDirectorDisplayName}
            />
        );
    }

    /* Homeowner and Professional share ticket + calendar success view. */
    return <HomeownerSuccessView data={data} />;
}
